import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import KeyBenefitModal from '../components/KeyBenefitModal'; // We might need a custom modal or adapt this one
import ConfirmDialog from '../components/ConfirmDialog';

// Since KeyBenefitModal might not support editing 'gradient_from' as 'Step Number', 
// I will create a custom modal inside this file or reused KeyBenefitModal if it's flexible enough.
// Checking DSAPartnerProgramHowItWorksPage.js, it has an internal 'renderIcon' and uses inline modal? 
// No, it uses 'isModalOpen' and renders a form inline in the file! (See Step 153, lines 287-387).
// So I can just copy that structure and adapt the form to edit 'Step Number' instead of 'Gradient'.

import * as Icons from '@heroicons/react/24/outline';
import { Plus, Pencil, Trash2, Save } from 'lucide-react';

const DSAOnboardingStepsPage = () => {
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [steps, setSteps] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Simple and Streamlined Onboarding',
        section_subtitle: 'Getting Started',
        section_description: 'We follow a clear, structured onboarding process to ensure compliance and smooth engagement from day one.'
    });

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStep, setEditingStep] = useState(null);
    const [modalData, setModalData] = useState({
        title: '',
        description: '',
        icon_name: 'DocumentTextIcon',
        gradient_from: '01', // Reusing for Step Number
        display_order: 0,
        is_active: true
    });

    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
    const loanType = 'dsa_onboarding_steps';

    // Available Icons (Lucide names as per DSAOnboarding.js usage, but Admin uses Heroicons usually. 
    // I need to be careful. The Seed used Lucide names. 
    // I should provide a list of Lucide names or generic ones.)
    // DSAOnboarding.js uses: FileText, FileCheck, Shield, BookOpen, TrendingUp.
    const iconOptions = [
        'FileText',
        'FileCheck',
        'Shield',
        'BookOpen',
        'TrendingUp',
        'Users',
        'CheckCircle',
        'Clock'
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, stepsRes] = await Promise.all([
                axios.get(`/api/admin/key-benefits/settings/${loanType}`),
                axios.get(`/api/admin/key-benefits/${loanType}`)
            ]);

            if (settingsRes.data.success && settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }

            if (stepsRes.data.success) {
                setSteps(stepsRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Settings Handlers
    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        setSavingSettings(true);
        try {
            await axios.put(`/api/admin/key-benefits/settings/${loanType}`, {
                ...settings
            });
            toast.success('Section settings saved');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    // Step Handlers
    const handleAddNew = () => {
        setEditingStep(null);
        setModalData({
            title: '',
            description: '',
            icon_name: 'FileText',
            gradient_from: String(steps.length + 1).padStart(2, '0'), // Default next number
            display_order: steps.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (step) => {
        setEditingStep(step);
        setModalData({
            title: step.title,
            description: step.description || '',
            icon_name: step.icon_name,
            gradient_from: step.gradient_from || '01', // Step Number
            display_order: step.display_order,
            is_active: step.is_active
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteConfirm.id}`);
            toast.success('Step deleted successfully');
            fetchData();
        } catch (error) {
            console.error('Error deleting step:', error);
            toast.error('Failed to delete step');
        } finally {
            setDeleteConfirm({ isOpen: false, id: null });
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            // We use gradient_from to store the Step Number/Caption
            const payload = {
                ...modalData,
                loan_type: loanType,
                gradient_to: '' // Unused
            };

            if (editingStep) {
                await axios.put(`/api/admin/key-benefits/${editingStep.id}`, payload);
                toast.success('Step updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', payload);
                toast.success('Step added successfully');
            }

            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving step:', error);
            toast.error('Failed to save step');
        }
    };

    // Helper to render icon preview (just simple text here since we store Lucide names)
    // Real rendering depends on frontend map.
    const renderIconPreview = (iconName) => {
        return <div className="text-xs font-mono">{iconName}</div>;
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Getting Started</h1>
                    <p className="text-slate-500">Manage the onboarding steps</p>
                </div>
            </div>

            {/* Settings Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Section Settings</h2>
                <form onSubmit={saveSettings} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={settings.section_title}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Simple and Streamlined Onboarding"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Subtitle (Tag)</label>
                        <input
                            type="text"
                            name="section_subtitle"
                            value={settings.section_subtitle}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Getting Started"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Description</label>
                        <textarea
                            name="section_description"
                            value={settings.section_description}
                            onChange={handleSettingsChange}
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Describe the onboarding process..."
                        />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={savingSettings}
                            className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 transition-colors"
                        >
                            <Save size={18} />
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Steps List */}
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-bold text-slate-800">Onboarding Steps</h2>
                    <button
                        onClick={handleAddNew}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors"
                    >
                        <Plus size={18} /> Add New Step
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {steps.map((step, index) => (
                        <div key={step.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow relative flex gap-4">

                            {/* Step Number Display */}
                            <div className="w-16 h-16 rounded-xl bg-slate-100 flex flex-col items-center justify-center flex-shrink-0 text-primary-600">
                                <span className="text-xl font-bold">{step.gradient_from}</span>
                            </div>

                            <div className="flex-1">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-slate-800">{step.title}</h3>
                                    <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-500">
                                        {step.icon_name}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-500 mb-4">{step.description}</p>

                                <div className="flex justify-end gap-2 pt-2 border-t border-slate-50">
                                    <button onClick={() => handleEdit(step)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                        <Pencil size={16} />
                                    </button>
                                    <button onClick={() => handleDelete(step.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Edit/Add Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                        <h2 className="text-xl font-bold mb-6">{editingStep ? 'Edit Step' : 'Add New Step'}</h2>
                        <form onSubmit={handleModalSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Step Number / Caption</label>
                                    <input
                                        type="text"
                                        value={modalData.gradient_from}
                                        onChange={(e) => setModalData({ ...modalData, gradient_from: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                        placeholder="01"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={modalData.display_order}
                                        onChange={(e) => setModalData({ ...modalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Title</label>
                                <input
                                    type="text"
                                    value={modalData.title}
                                    onChange={(e) => setModalData({ ...modalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Description</label>
                                <textarea
                                    value={modalData.description}
                                    onChange={(e) => setModalData({ ...modalData, description: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl"
                                    rows={3}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Icon Name (Lucide)</label>
                                <select
                                    value={modalData.icon_name}
                                    onChange={(e) => setModalData({ ...modalData, icon_name: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl font-mono"
                                >
                                    {iconOptions.map(icon => (
                                        <option key={icon} value={icon}>{icon}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                                <input
                                    type="checkbox"
                                    checked={modalData.is_active}
                                    onChange={(e) => setModalData({ ...modalData, is_active: e.target.checked })}
                                    className="w-5 h-5 rounded text-primary-600"
                                />
                                <span className="font-medium">Active</span>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2 border rounded-xl hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Step"
                message="Are you sure you want to delete this step?"
            />
        </div>
    );
};

export default DSAOnboardingStepsPage;
